import FollowerIcon from 'assets/discovery/followers-icon.png';
import EngIcon from 'assets/discovery/Eng-Rate-icon.png';
import AvgViewIcon from 'assets/discovery/Avg-View-icon.png';
import AvgEngIcon from 'assets/discovery/Avg-Eng-Icon.png';
import CommentIcon from 'assets/discovery/comment-icon.png';
import LikeIcon from 'assets/Thumbs_Up.png';
import countertostring from '../../../common';
import { ui } from '../shared/insightUi';

import StatsCard from './components/StatsCard';

function formatStatValue(val, useCompact = false) {
  if (val == null || val === '' || val === '–') return val;
  const num = Number(val);
  if (isNaN(num)) return val;
  return useCompact && num >= 1000 ? countertostring(num) : num >= 1000 ? num.toLocaleString() : String(val);
}

// Only one of the all_/organic_/paid_ prefixes is present per response,
// depending on which mode StatsFilters requested.
function pickStat(filteredData, suffix) {
  return (
    filteredData?.[`all_posts_${suffix}`] ??
    filteredData?.[`organic_posts_${suffix}`] ??
    filteredData?.[`paid_posts_${suffix}`] ??
    null
  );
}

// Plain flex-wrap with fixed-size tiles, not MUI Grid: nested Grid spacing
// (negative margins) made the KPI rows overlap. Tiles keep one compact size;
// the container simply grows a row taller when they wrap.
const tilesGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: 12,
};

function BasicStats({
  followers,
  platform,
  subscriber_count,
  engagement_rate,
  avg_views,
  paid_post_performance,
  engagements,
  audience_followers_credibility,
  filteredData,
  avg_comments,
  avg_likes,
  averagewatchtime,
  is_brandconnect,
  dump,
  bc_insight,
  bc_insight_60,
  bc_insight_90,
  bc_insight_lifetime,
  bc_public_data,
  period = '28',
  contentType = 'overall',
  filterViaTypes = 'all',
}) {
      const dataSharingEnabled =
  String(bc_public_data?.dataSharingState || '')
    .toUpperCase()
    .includes('ENABLED') ||
  String(bc_public_data?.dataSharingState || '')
    .toUpperCase()
    .includes('GIVEN') ||
  String(dump?.data_sharing_state || '')
    .toUpperCase()
    .includes('ENABLED') ||
  String(dump?.data_sharing_state || '')
    .toUpperCase()
    .includes('GIVEN');
  const isBC = is_brandconnect === 'yes' && dataSharingEnabled;
  const bcSource =
    period === '60'
      ? bc_insight_60
      : period === '90'
        ? bc_insight_90
        : period === 'lifetime'
          ? bc_insight_lifetime
          : bc_insight;

  // Change OVERALL -> SHORTS based on content type
  const isShortSelected = contentType;
  const channelStatsKey = isShortSelected === 'short' ? 'SHORTS' : isShortSelected === 'video' ? 'VOD' : 'OVERALL';
  const subCount = bc_public_data?.subscriberCount ?? subscriber_count ?? dump?.subscriber_count ?? followers;
  // const videoCount = bc_public_data?.videoCount ?? dump?.video_count;
  const videoCount = bcSource?.channelStats?.[channelStatsKey]?.aggregateStats?.videoCount

  const totalViews = bcSource?.channelStats?.[channelStatsKey]?.aggregateStats?.views ?? bc_public_data?.viewCount ?? dump?.views;
  const avgViewsBC = bcSource?.channelStats?.[channelStatsKey]?.averageStats?.averageViewsPerVideo;
  const agg = bcSource?.channelStats?.[channelStatsKey]?.aggregateStats;
  const avg = bcSource?.channelStats?.[channelStatsKey]?.averageStats;
  const avgCommentsBC = Math.round(avg?.averageCommentsPerVideo);
  // null when the selected posts have no views — e.g. the Instagram Posts
  // tab, where static posts have likes but nothing to divide them by.
  const erByViews = isBC ? null : pickStat(filteredData, 'engagement_rate_by_views');
  const getStatValue = (value) => value;

  // When the Posts tab is active on Instagram (non-BC), Avg Views is hidden.
  const isPostTab = !isBC && platform === 'instagram' && filterViaTypes === 'post';

  // A Videos/Shorts tab that matched no videos returns real zeros, which
  // would otherwise read as terrible performance rather than as an absence.
  // `considered` is missing from older cached responses, hence the explicit
  // === 0 rather than a falsy check.
  const isEmptyContentTab =
    !isBC &&
    platform === 'youtube' &&
    (filterViaTypes === 'video' || filterViaTypes === 'short') &&
    pickStat(filteredData, 'considered') === 0;

  if (isEmptyContentTab) {
    return (
      <div style={{ minHeight: 72, display: 'flex', alignItems: 'center', color: ui.inkSoft, fontSize: 13, fontFamily: ui.font }}>
        {filterViaTypes === 'short'
          ? 'No Shorts found for this channel.'
          : 'No videos found for this channel.'}
      </div>
    );
  }

  return (
    <div style={tilesGridStyle}>
      {isBC ? (
        <StatsCard
          value={getStatValue(formatStatValue(subCount, true))}
          label={'Subscribers'}
          iconSrc={FollowerIcon}
        />
      ) : (
        <>
          <StatsCard value={getStatValue(followers)} label={'Followers'} iconSrc={FollowerIcon} />
          <StatsCard
            value={getStatValue(
              filteredData.all_posts_avg_likes
                ? filteredData.all_posts_avg_likes
                : filteredData.organic_posts_avg_likes
                ? filteredData.organic_posts_avg_likes
                : filteredData.paid_posts_avg_likes
                ? filteredData.paid_posts_avg_likes
                : 0
            )}
            label={'Avg Likes'}
            iconSrc={LikeIcon}
          />
        </>
      )}
      <StatsCard
        value={getStatValue(
          filteredData.all_posts_engagement_rate
            ? filteredData.all_posts_engagement_rate
            : filteredData.organic_posts_engagement_rate
            ? filteredData.organic_posts_engagement_rate
            : filteredData.paid_posts_engagement_rate
        )}
        label={isBC || platform !== 'instagram' ? 'ER% (Subscribers)' : 'ER% (Followers)'}
        iconSrc={EngIcon}
      />
      {isBC && (
        <StatsCard
          value={
            bcSource?.channelStats?.[channelStatsKey]?.aggregateStats?.engagementRate != null
              ? ((getStatValue(bcSource.channelStats[channelStatsKey].aggregateStats.engagementRate).toFixed(4) * 100).toLocaleString()) + '%'
              : '–'
          }
          label={!isBC ? 'Engagement Rate' : `ER% (Views)`}
          iconSrc={EngIcon}
        />
      )}
      {!isBC && erByViews && (
        <StatsCard value={getStatValue(erByViews)} label={'ER% (Views)'} iconSrc={EngIcon} />
      )}

      {isBC && avgViewsBC != null ? (
        <StatsCard value={getStatValue(formatStatValue(avgCommentsBC, true))} label={'Avg Comments'} iconSrc={CommentIcon} />
      ) : !isBC ? (
        <StatsCard
          value={getStatValue(
            filteredData.all_posts_avg_comments
              ? filteredData.all_posts_avg_comments
              : filteredData.organic_posts_avg_comments
              ? filteredData.organic_posts_avg_comments
              : filteredData.paid_posts_avg_comments
              ? filteredData.paid_posts_avg_comments
              : 0
          )}
          label={'Avg Comments'}
          iconSrc={CommentIcon}
        />
      ) : null}

      {isBC && avgViewsBC != null ? (
        <StatsCard value={getStatValue(formatStatValue(avgViewsBC, true))} label={'Avg Views'} iconSrc={AvgViewIcon} />
      ) : !isBC ? (
        !isPostTab && (
          <StatsCard
            value={getStatValue(
              filteredData.all_posts_avg_views
                ? filteredData.all_posts_avg_views
                : filteredData.organic_posts_avg_views
                ? filteredData.organic_posts_avg_views
                : filteredData.paid_posts_avg_views
                ? filteredData.paid_posts_avg_views
                : 0
            )}
            label={'Avg Views'}
            iconSrc={AvgViewIcon}
          />
        )
      ) : null}
      {isBC && videoCount != null && (
        <StatsCard
          value={getStatValue(formatStatValue(videoCount, true))}
          label={'Videos'}
          iconSrc={AvgViewIcon}
        />
      )}
      {isBC && totalViews != null && (
        <StatsCard
          value={getStatValue(formatStatValue(totalViews, true))}
          label={'Total Views'}
          iconSrc={AvgViewIcon}
        />
      )}
      {isBC && agg?.likes != null && (
        <StatsCard value={getStatValue(formatStatValue(agg.likes, true))} label={'Likes'} iconSrc={LikeIcon} />
      )}
      {isBC && agg?.comments != null && (
        <StatsCard value={getStatValue(formatStatValue(agg.comments, true))} label={'Comments'} iconSrc={CommentIcon} />
      )}
      {isBC && agg?.uniqueViewers != null && (
        <StatsCard value={getStatValue(formatStatValue(agg.uniqueViewers, true))} label={'Unique viewers'} iconSrc={FollowerIcon} />
      )}
      {isBC && avg?.averageLikesPerVideo != null && (
        <StatsCard value={getStatValue(formatStatValue(avg.averageLikesPerVideo, true))} label={'Avg Likes'} iconSrc={LikeIcon} />
      )}
      {isBC && avg?.averageReachPerVideo != null && (
        <StatsCard value={getStatValue(formatStatValue(avg.averageReachPerVideo, true))} label={'Reach'} iconSrc={AvgViewIcon} />
      )}
      {!isBC && platform !== "instagram" && (
        <StatsCard
          value={getStatValue(
            filteredData.all_posts_averagewatchtime
              ? filteredData.all_posts_averagewatchtime
              : filteredData.organic_posts_averagewatchtime
              ? filteredData.organic_posts_averagewatchtime
              : filteredData.paid_posts_averagewatchtime
              ? filteredData.paid_posts_averagewatchtime
              : 0
          )}
          label={'Avg watch time'}
          iconSrc={AvgViewIcon}
        />
      )}
      {!isBC && platform === "instagram" && (
        <StatsCard
          value={getStatValue(
            filteredData.all_posts_avg_engagement
              ? filteredData.all_posts_avg_engagement
              : filteredData.organic_posts_avg_engagement
              ? filteredData.organic_posts_avg_engagement
              : filteredData.paid_posts_avg_engagement
          )}
          label={'Avg Engagement'}
          iconSrc={AvgEngIcon}
        />
      )}
      {!isBC && platform !== "youtube" && (
        <StatsCard
          value={audience_followers_credibility ? parseFloat(audience_followers_credibility * 100).toFixed(2) + '%' : ''}
          label={'Audience Credibility'}
          iconSrc={FollowerIcon}
        />
      )}
    </div>
  );
}

export default BasicStats;
